import { useRef, useState } from "react";
import { toast } from "sonner";
import { Video, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { HmsCall } from "@/components/HmsCall";

const CHECKS = [["access_key", "App Access Key set"], ["secret", "App Secret set"], ["template_id", "Template ID set"], ["credentials", "100ms accepts the credentials"], ["roles", "Template has doctor / patient / audio roles"]];

export function VideoTest() {
  const [busy, setBusy] = useState(false);
  const [h, setH] = useState(null);
  const [room, setRoom] = useState(null);
  const [status, setStatus] = useState("idle");
  const call = useRef(null);

  const check = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/video/health"); setH(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => {
    call.current?.leave().catch(() => {});
    if (room) api.post(`/admin/video/test-room/${room.room_id}/close`).catch(() => {});
    setRoom(null); setStatus("idle");
  };
  const start = async () => {
    setStatus("connecting");
    try { const { data } = await api.post("/admin/video/test-room"); setRoom(data); } catch (e) { setStatus("error"); toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-3 rounded-xl border border-[#E2E8ED] p-3" data-testid="video-test-panel">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={check} disabled={busy} data-testid="video-health-btn">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Video className="h-3.5 w-3.5 mr-1.5" />}Test video</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={start} disabled={status === "connecting"} data-testid="video-test-room-btn">Open test room</Button>
      </div>
      {h && <div className="mt-3 space-y-1 text-xs" data-testid="video-health-result">
        <p className={`font-semibold ${h.ok ? "text-[#086B2D]" : "text-red-700"}`} data-testid="video-health-overall">{h.ok ? "Video is ready" : "Video is NOT ready"}</p>
        {CHECKS.map(([k, l]) => <p key={k} className="flex items-center gap-1.5" data-testid={`video-check-${k}`}>{h[k] ? <CheckCircle2 className="h-3.5 w-3.5 text-[#086B2D]" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />}{l}</p>)}
        {h.error && <p className="text-red-700" data-testid="video-health-error">{h.error}</p>}
        <p className="muted">Template {h.template_id_value} · Key {h.access_key_value}</p>
      </div>}
      <Dialog open={!!room || status === "error"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Video test room</DialogTitle>
            <DialogDescription data-testid="video-test-room-status">{status === "live" ? "Connected – 100ms accepted the room pass. You should see your own camera." : status === "error" ? "Could not open the test room. Check the 100ms details." : "Connecting to 100ms… allow camera and microphone if asked."}</DialogDescription></DialogHeader>
          <div className="relative h-[60vh] bg-[#0E1A24] rounded-xl overflow-hidden">
            {room && <div className="absolute inset-0"><HmsCall data={room} callRef={call} onJoin={() => setStatus("live")} onLeave={close} onError={() => { setStatus("error"); if (room) api.post(`/admin/video/test-room/${room.room_id}/close`).catch(() => {}); }} /></div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
